import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, download, qs } from '../lib/api';
import { addDays, date, dateTime, eur, iban, today } from '../lib/format';
import { useSession } from '../lib/session';
import { Badge, Confirm, Empty, ErrorBox, Field, Loading, Modal, PageHead, Tabs, useAction, useLoad } from '../components/ui';
import { UploadModal } from '../components/Upload';
import { MessageEditor } from './InvoiceDetail';

function NeedCompany() {
  return <Empty>Elige una empresa en la parte superior para continuar.</Empty>;
}

// ───────────────────────── Contabilidad ─────────────────────────
export function Accounting() {
  const s = useSession();
  const { busy, run } = useAction();
  const [sel, setSel] = useState<Set<string>>(new Set());
  const templates = useLoad(() => api.get('/export-templates'), []);
  const company = useLoad(async () => (s.companyId ? (await api.get('/companies')).find((c: any) => c.id === s.companyId) : null), [s.companyId]);
  const [target, setTarget] = useState<string>('');
  const approved = useLoad(() => (s.companyId ? api.get(`/invoices${qs({ companyId: s.companyId, status: 'approved', pageSize: 200, sort: 'issue' })}`) : Promise.resolve(null)), [s.companyId]);
  const batches = useLoad(() => api.get('/exports'), []);
  const [voiding, setVoiding] = useState<string | null>(null);
  if (!s.companyId) return <><PageHead title="Contabilidad" /><NeedCompany /></>;
  const items: any[] = approved.data?.items ?? [];
  const chosen = target || company.data?.settings?.exportTarget || 'csv-facturas';
  const tpl = (templates.data ?? []).find((t: any) => t.id === chosen);
  const doExport = async () => {
    const r = await run(() => api.post('/exports', { companyId: s.companyId, invoiceIds: [...sel], target: chosen }), 'Exportación creada');
    if (r) {
      setSel(new Set());
      approved.reload();
      batches.reload();
      if (!['holded', 'odoo'].includes(chosen)) await run(() => download(`/exports/${r.batchId}/download`));
      else alert(r.results.map((x: any) => `${x.ok ? '✓' : '✗'} ${x.externalId ?? ''} ${x.error ?? ''}`).join('\n'));
    }
  };
  return (
    <>
      <PageHead title="Contabilidad" subtitle="Exporta las facturas aprobadas a tu programa contable. Cada factura se exporta una sola vez." />
      <div className="card stack">
        <div className="row wrap">
          <Field label="Formato de destino">
            <select value={chosen} onChange={(e) => setTarget(e.target.value)} style={{ minWidth: 320 }}>
              {(templates.data ?? []).map((t: any) => <option key={t.id} value={t.id}>{t.name}{t.validated ? '' : ' — validar antes de usar'}</option>)}
            </select>
          </Field>
          <div className="grow" />
          <button className="primary" disabled={busy || sel.size === 0} onClick={doExport}>Exportar {sel.size || ''} factura(s)</button>
        </div>
        {tpl && !tpl.validated && <div className="alert warn">{tpl.notes ?? 'Formato pendiente de validar con el importador de tu programa: prueba primero con un lote pequeño.'}</div>}
        {approved.loading && !approved.data ? <Loading /> : items.length === 0 ? <Empty>No hay facturas aprobadas pendientes de contabilizar.</Empty> : (
          <div className="table-wrap"><table>
            <thead><tr><th className="check"><input type="checkbox" aria-label="Todas" checked={sel.size === items.length} onChange={(e) => setSel(e.target.checked ? new Set(items.map((i) => i.id)) : new Set())} /></th><th>Fecha</th><th>Proveedor</th><th>Número</th><th>Cuenta</th><th>C. coste</th><th className="num">Total</th></tr></thead>
            <tbody>{items.map((i) => (
              <tr key={i.id}>
                <td className="check"><input type="checkbox" aria-label="Seleccionar" checked={sel.has(i.id)} onChange={() => setSel((x) => { const n = new Set(x); n.has(i.id) ? n.delete(i.id) : n.add(i.id); return n; })} /></td>
                <td>{date(i.issue_date)}</td><td><Link to={`/facturas/${i.id}`}>{i.vendor_name}</Link></td><td className="mono">{i.number}</td><td className="mono">{i.account}</td><td>{i.cost_center ?? ''}</td><td className="num">{eur(i.total)}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
      <div className="spacer" />
      <h2>Lotes exportados</h2>
      {(batches.data ?? []).length === 0 ? <Empty>Aún no hay exportaciones.</Empty> : (
        <div className="table-wrap"><table>
          <thead><tr><th>Fecha</th><th>Destino</th><th className="num">Facturas</th><th>Estado</th><th>Por</th><th /></tr></thead>
          <tbody>{batches.data.map((b: any) => (
            <tr key={b.id}>
              <td>{dateTime(b.created_at)}</td><td>{(templates.data ?? []).find((t: any) => t.id === b.target)?.name ?? b.target}</td><td className="num">{b.invoices}</td>
              <td>{b.status === 'voided' ? <Badge>Anulado</Badge> : b.status === 'failed' ? <Badge tone="error">Fallido</Badge> : <Badge tone="ok">Entregado</Badge>}</td><td>{b.created_by_name}</td>
              <td className="row">{b.storage_key && <button className="small" onClick={() => run(() => download(`/exports/${b.id}/download`))}>Descargar</button>}
                {b.status !== 'voided' && s.can('invoice.export') && <button className="small ghost" onClick={() => setVoiding(b.id)}>Anular lote</button>}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
      {voiding && <Confirm title="Anular lote de exportación" needReason danger text="Las facturas del lote volverán a estar pendientes de contabilizar. Hazlo solo si el fichero no se llegó a importar o si has borrado los asientos en tu programa."
        onClose={() => setVoiding(null)} onConfirm={async (reason) => { if (await run(() => api.post(`/exports/${voiding}/void`, { reason }), 'Lote anulado')) { setVoiding(null); batches.reload(); approved.reload(); } }} />}
    </>
  );
}

// ───────────────────────── Factura electrónica ─────────────────────────
export function EInvoice() {
  const s = useSession();
  const { busy, run } = useAction();
  const [onlyPending, setOnlyPending] = useState(true);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const { data, error, loading, reload } = useLoad(() => api.get(`/einvoice/events${qs({ companyId: s.companyId, pending: onlyPending || undefined })}`), [s.companyId, onlyPending]);
  const rows: any[] = data ?? [];
  return (
    <>
      <PageHead title="Factura electrónica" subtitle="Estados que debes comunicar de cada factura electrónica recibida: aceptación o rechazo, y pago. Plazo legal: 4 días (sin contar fines de semana ni festivos nacionales)." />
      <div className="alert info" style={{ marginBottom: 12 }}>
        Conforme crea los estados solos al aprobar, rechazar o pagar una factura electrónica. Si tu asesoría presenta por ti en la solución pública de la AEAT, descarga el fichero y márcalos como comunicados.
      </div>
      <div className="row wrap" style={{ marginBottom: 12 }}>
        <label className="check"><input type="checkbox" checked={onlyPending} onChange={(e) => setOnlyPending(e.target.checked)} /> Solo pendientes</label>
        <div className="grow" />
        {s.can('einvoice.submit') && <>
          <button disabled={!sel.size || busy} onClick={() => run(() => download('/einvoice/events/export', { ids: [...sel] }))}>Descargar fichero ({sel.size})</button>
          <button className="primary" disabled={!sel.size || busy} onClick={async () => { if (await run(() => api.post('/einvoice/events/manual-submitted', { ids: [...sel] }), 'Marcados como comunicados')) { setSel(new Set()); reload(); } }}>Marcar como comunicados</button>
        </>}
      </div>
      <ErrorBox error={error} />
      {loading && !data ? <Loading /> : rows.length === 0 ? <Empty>No hay estados pendientes de comunicar.</Empty> : (
        <div className="table-wrap"><table>
          <thead><tr><th className="check" /><th>Factura</th><th>Proveedor</th><th>Estado</th><th>Fecha</th><th>Comunicar antes de</th><th>Situación</th><th>Canal</th></tr></thead>
          <tbody>{rows.map((e) => (
            <tr key={e.id}>
              <td className="check"><input type="checkbox" aria-label="Seleccionar" checked={sel.has(e.id)} onChange={() => setSel((x) => { const n = new Set(x); n.has(e.id) ? n.delete(e.id) : n.add(e.id); return n; })} /></td>
              <td><Link to={`/facturas/${e.invoice_id}`} className="mono">{e.number}</Link><div className="hint">{e.company_name}</div></td>
              <td>{e.vendor_name}<div className="muted mono">{e.vendor_nif}</div></td>
              <td>{e.statusLabel}{e.reason && <div className="hint">{e.reason}</div>}</td>
              <td>{date(e.event_date)}</td>
              <td>{date(e.due_by)} {e.submission_status !== 'acknowledged' && (e.deadline === 'overdue' ? <Badge tone="error">Fuera de plazo</Badge> : e.deadline === 'due_soon' ? <Badge tone="warn">Vence hoy</Badge> : null)}</td>
              <td>{e.submission_status === 'acknowledged' ? <Badge tone="ok">Comunicado</Badge> : e.submission_status === 'error' ? <><Badge tone="error">Error</Badge> <button className="small" onClick={async () => { if (await run(() => api.post(`/einvoice/events/${e.id}/retry`))) reload(); }}>Reintentar</button></> : <Badge tone="warn">Pendiente</Badge>}</td>
              <td>{({ manual: 'Manual / asesoría', http: 'Plataforma', sandbox: 'Pruebas' } as any)[e.channel]}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
    </>
  );
}

// ───────────────────────── Pagos ─────────────────────────
export function Payments() {
  const s = useSession();
  const { busy, run } = useAction();
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [exec, setExec] = useState(addDays(today(), 2));
  const payable = useLoad(() => (s.companyId && s.can('payment.prepare') ? api.get(`/payments/payable?companyId=${s.companyId}`) : Promise.resolve([])), [s.companyId]);
  const batches = useLoad(() => api.get('/payments/batches'), []);
  if (!s.companyId) return <><PageHead title="Pagos" /><NeedCompany /></>;
  const rows: any[] = payable.data ?? [];
  const total = rows.filter((r) => sel.has(r.id)).reduce((a, r) => a + Number(r.total), 0);
  const reloadAll = () => { payable.reload(); batches.reload(); };
  return (
    <>
      <PageHead title="Pagos" subtitle="Prepara remesas de transferencias SEPA que tú revisas y subes a tu banco. Conforme no mueve dinero: no tiene acceso a tus cuentas ni ordena pagos." />
      <div className="card stack">
        <div className="row wrap">
          <Field label="Fecha de ejecución"><input type="date" min={today()} value={exec} onChange={(e) => setExec(e.target.value)} /></Field>
          <div className="grow" />
          <span>Seleccionado: <strong>{eur(total)}</strong></span>
          {s.can('payment.prepare') && <button className="primary" disabled={busy || !sel.size} onClick={async () => { if (await run(() => api.post('/payments/batches', { companyId: s.companyId, invoiceIds: [...sel], executionDate: exec }), 'Remesa preparada: falta que la apruebe otra persona')) { setSel(new Set()); reloadAll(); } }}>Preparar remesa</button>}
        </div>
        {payable.error && <ErrorBox error={payable.error} />}
        {rows.length === 0 ? <Empty>No hay facturas aprobadas pendientes de pago.</Empty> : (
          <div className="table-wrap"><table>
            <thead><tr><th className="check" /><th>Vencimiento</th><th>Proveedor</th><th>Factura</th><th>IBAN</th><th className="num">Importe</th><th /></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.id} style={{ opacity: r.blockedReason ? 0.75 : 1 }}>
                <td className="check"><input type="checkbox" aria-label="Seleccionar" disabled={Boolean(r.blockedReason)} checked={sel.has(r.id)} onChange={() => setSel((x) => { const n = new Set(x); n.has(r.id) ? n.delete(r.id) : n.add(r.id); return n; })} /></td>
                <td>{date(r.due_date)} {r.due_date && r.due_date < today() && <Badge tone="error">Vencida</Badge>}</td>
                <td>{r.vendor_name}</td><td><Link to={`/facturas/${r.id}`} className="mono">{r.number}</Link></td>
                <td className="mono">{iban(r.iban)}</td><td className="num">{eur(r.total)}</td>
                <td>{r.blockedReason && <><Badge tone="warn">{r.blockedReason}</Badge>{/verificar|coincide/.test(r.blockedReason) && <div><Link to="/proveedores" className="hint">Verificar cuenta</Link></div>}</>}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
      <div className="spacer" />
      <h2>Remesas</h2>
      {(batches.data ?? []).length === 0 ? <Empty>Aún no hay remesas.</Empty> : (
        <div className="table-wrap"><table>
          <thead><tr><th>Ejecución</th><th className="num">Facturas</th><th className="num">Total</th><th>Estado</th><th>Preparó</th><th>Aprobó</th><th>Descargó</th><th /></tr></thead>
          <tbody>{batches.data.map((b: any) => (
            <tr key={b.id}>
              <td>{date(b.execution_date)}<div className="hint mono">{b.msg_id}</div></td><td className="num">{b.invoices}</td><td className="num">{eur(b.total)}</td>
              <td><Badge tone={({ draft: 'warn', approved: 'brand', downloaded: 'info', paid: 'ok', cancelled: '' } as any)[b.status]}>{({ draft: 'Por aprobar', approved: 'Aprobada', downloaded: 'Descargada', paid: 'Pagada', cancelled: 'Cancelada' } as any)[b.status]}</Badge></td>
              <td>{b.prepared_by_name}</td><td>{b.approved_by_name ?? '—'}</td><td>{b.downloaded_by_name ?? '—'}</td>
              <td className="row">
                {b.status === 'draft' && s.can('payment.approve') && (!b.strict_segregation || b.prepared_by !== s.me?.user.id) && <button className="small ok" disabled={busy} onClick={async () => { if (await run(() => api.post(`/payments/batches/${b.id}/approve`), 'Remesa aprobada')) reloadAll(); }}>Aprobar</button>}
                {['approved', 'downloaded'].includes(b.status) && s.can('payment.download') && (!b.strict_segregation || b.approved_by !== s.me?.user.id) && <button className="small" disabled={busy} onClick={async () => { await run(() => download(`/payments/batches/${b.id}/download`)); batches.reload(); }}>Descargar fichero</button>}
                {['draft', 'approved'].includes(b.status) && s.can('payment.prepare') && <button className="small ghost" disabled={busy} onClick={async () => { if (confirm('¿Cancelar la remesa?') && await run(() => api.post(`/payments/batches/${b.id}/cancel`), 'Remesa cancelada')) reloadAll(); }}>Cancelar</button>}
              </td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
      <p className="hint">Segregación de funciones: quien prepara una remesa no puede aprobarla, y quien la aprueba no puede descargarla. Las facturas se marcan como pagadas al importar el extracto del banco (menú Banco).</p>
    </>
  );
}

// ───────────────────────── Banco ─────────────────────────
export function Bank() {
  const s = useSession();
  const { busy, run } = useAction();
  const [onlyOpen, setOnlyOpen] = useState(true);
  const [match, setMatch] = useState<any>(null);
  const movs = useLoad(() => (s.companyId ? api.get(`/bank/movements${qs({ companyId: s.companyId, unmatched: onlyOpen || undefined })}`) : Promise.resolve([])), [s.companyId, onlyOpen]);
  if (!s.companyId) return <><PageHead title="Banco" /><NeedCompany /></>;
  const importFile = async (f: File) => {
    const fd = new FormData();
    fd.append('companyId', s.companyId!);
    fd.append('file', f, f.name);
    const r = await run(() => api.form('/bank/import', fd));
    if (r) { alert(`${r.movements} movimientos leídos, ${r.inserted} nuevos. ${r.matched} pago(s) conciliado(s) con su factura${r.suggestions ? ` y ${r.suggestions} sugerencia(s) por revisar` : ''}.`); movs.reload(); }
  };
  return (
    <>
      <PageHead title="Banco" subtitle="Importa el extracto (Norma 43 o CAMT.053) para marcar las facturas como pagadas automáticamente."
        actions={s.can('bank.import') && <label className="btn primary">Importar extracto<input type="file" hidden accept=".n43,.txt,.aeb,.csb,.xml" onChange={(e) => e.target.files?.[0] && importFile(e.target.files[0])} /></label>} />
      <label className="check" style={{ marginBottom: 12 }}><input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} /> Solo sin conciliar</label>
      {movs.loading && !movs.data ? <Loading /> : (movs.data ?? []).length === 0 ? <Empty>No hay movimientos.</Empty> : (
        <div className="table-wrap"><table>
          <thead><tr><th>Fecha</th><th>Concepto</th><th>Referencia</th><th className="num">Importe</th><th>Factura</th><th /></tr></thead>
          <tbody>{movs.data.map((m: any) => (
            <tr key={m.id}>
              <td>{date(m.booking_date)}</td><td>{m.concept}</td><td className="mono">{m.reference}</td>
              <td className="num" style={{ color: m.amount < 0 ? 'var(--error)' : 'var(--accent)' }}>{eur(m.amount)}</td>
              <td>{m.matched_invoice_id ? <Link to={`/facturas/${m.matched_invoice_id}`}>{m.invoice_number} · {m.vendor_name}</Link> : m.match_confidence ? <Badge tone="warn">Sugerida ({Math.round(m.match_confidence * 100)} %)</Badge> : '—'}</td>
              <td>{!m.matched_invoice_id && m.amount < 0 && s.can('payment.mark_paid') && <button className="small" disabled={busy} onClick={() => setMatch(m)}>Emparejar</button>}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
      {match && <MatchModal mov={match} onClose={() => { setMatch(null); movs.reload(); }} />}
    </>
  );
}

function MatchModal({ mov, onClose }: { mov: any; onClose: () => void }) {
  const s = useSession();
  const { busy, run } = useAction();
  const cands = useLoad(() => api.get(`/invoices${qs({ companyId: s.companyId, status: 'approved,exported,scheduled', pageSize: 200 })}`), []);
  const amount = Math.abs(mov.amount);
  const items = (cands.data?.items ?? []).sort((a: any, b: any) => Math.abs(a.total - amount) - Math.abs(b.total - amount));
  return (
    <Modal title={`Emparejar cargo de ${eur(amount)}`} onClose={onClose}>
      <p className="hint">{date(mov.booking_date)} · {mov.concept}</p>
      {cands.loading && !cands.data ? <Loading /> : items.length === 0 ? <Empty>No hay facturas pendientes de pago.</Empty> : (
        <table><tbody>{items.slice(0, 20).map((i: any) => (
          <tr key={i.id}><td>{i.vendor_name}<div className="mono hint">{i.number}</div></td><td className="num">{eur(i.total)}</td>
            <td><button className="small" disabled={busy || Math.abs(i.total - amount) > 0.005} onClick={async () => { if (await run(() => api.post(`/bank/movements/${mov.id}/match`, { invoiceId: i.id }), 'Factura marcada como pagada')) onClose(); }}>Es esta</button></td></tr>
        ))}</tbody></table>
      )}
    </Modal>
  );
}

// ───────────────────────── Albaranes y pedidos ─────────────────────────
export function DeliveryNotes() {
  const s = useSession();
  const { busy, run } = useAction();
  const [tab, setTab] = useState<'albaranes' | 'pedidos'>('albaranes');
  const [upload, setUpload] = useState(false);
  const [csv, setCsv] = useState('');
  const notes = useLoad(() => (s.companyId ? api.get(`/delivery-notes?companyId=${s.companyId}`) : Promise.resolve([])), [s.companyId]);
  const orders = useLoad(() => (s.companyId ? api.get(`/purchase-orders?companyId=${s.companyId}`) : Promise.resolve([])), [s.companyId]);
  if (!s.companyId) return <><PageHead title="Albaranes y pedidos" /><NeedCompany /></>;
  return (
    <>
      <PageHead title="Albaranes y pedidos" subtitle="Conforme compara cada factura con los albaranes y pedidos que cita: cantidades y precios." actions={<button className="primary" onClick={() => setUpload(true)}>Subir albaranes</button>} />
      <Tabs value={tab} onChange={setTab} items={[{ id: 'albaranes', label: 'Albaranes' }, { id: 'pedidos', label: 'Pedidos' }]} />
      {tab === 'albaranes' && ((notes.data ?? []).length === 0 ? <Empty>Sube fotos o PDF de los albaranes; Conforme los leerá y los cruzará con las facturas.</Empty> : (
        <div className="table-wrap"><table>
          <thead><tr><th>Número</th><th>Fecha</th><th>NIF proveedor</th><th className="num">Líneas</th><th>Factura</th></tr></thead>
          <tbody>{notes.data.map((n: any) => <tr key={n.id}><td className="mono">{n.number}</td><td>{date(n.note_date)}</td><td className="mono">{n.vendor_nif ?? '—'}</td><td className="num">{n.lines.length}</td><td>{n.invoice_number ?? '—'}</td></tr>)}</tbody>
        </table></div>
      ))}
      {tab === 'pedidos' && (
        <div className="stack">
          <div className="card flat stack">
            <h3>Importar pedidos (CSV)</h3>
            <p className="hint">Cabeceras: <code>numero;nif;total;fecha</code></p>
            <textarea value={csv} onChange={(e) => setCsv(e.target.value)} />
            <div><button disabled={busy || !csv} onClick={async () => { const r = await run(() => api.post('/purchase-orders/import', { companyId: s.companyId, csv })); if (r) { alert(`${r.imported} pedidos importados`); setCsv(''); orders.reload(); } }}>Importar</button></div>
          </div>
          {(orders.data ?? []).length > 0 && (
            <div className="table-wrap"><table>
              <thead><tr><th>Pedido</th><th>Fecha</th><th>NIF</th><th className="num">Total</th><th>Estado</th></tr></thead>
              <tbody>{orders.data.map((o: any) => <tr key={o.id}><td className="mono">{o.number}</td><td>{date(o.order_date)}</td><td className="mono">{o.vendor_nif}</td><td className="num">{eur(o.total)}</td><td>{o.status}</td></tr>)}</tbody>
            </table></div>
          )}
        </div>
      )}
      {upload && <UploadModal kind="delivery_note" companyId={s.companyId} onClose={() => { setUpload(false); setTimeout(notes.reload, 1500); }} />}
    </>
  );
}

// ───────────────────────── Mensajes ─────────────────────────
export function Messages() {
  const { data, loading, reload } = useLoad(() => api.get('/messages'), []);
  const [edit, setEdit] = useState<any>(null);
  return (
    <>
      <PageHead title="Mensajes a proveedores" subtitle="Borradores y correos enviados desde las facturas." />
      {loading && !data ? <Loading /> : (data ?? []).length === 0 ? <Empty>No hay mensajes.</Empty> : (
        <div className="table-wrap"><table>
          <thead><tr><th>Fecha</th><th>Proveedor</th><th>Asunto</th><th>Para</th><th>Estado</th><th /></tr></thead>
          <tbody>{data.map((m: any) => (
            <tr key={m.id}><td>{dateTime(m.created_at)}</td><td>{m.vendor_name}</td><td>{m.subject}{m.invoice_id && <div><Link to={`/facturas/${m.invoice_id}`} className="hint">Factura {m.invoice_number}</Link></div>}</td><td>{m.to_address ?? '—'}</td>
              <td><Badge tone={m.status === 'sent' ? 'ok' : m.status === 'draft' ? 'warn' : ''}>{m.status === 'sent' ? 'Enviado' : m.status === 'draft' ? 'Borrador' : 'Descartado'}</Badge></td>
              <td>{m.status === 'draft' && <button className="small" onClick={() => setEdit(m)}>Revisar y enviar</button>}</td></tr>
          ))}</tbody>
        </table></div>
      )}
      {edit && <MessageEditor m={edit} onClose={() => setEdit(null)} onSent={() => { setEdit(null); reload(); }} />}
    </>
  );
}

// ───────────────────────── Auditoría ─────────────────────────
export function Audit() {
  const { busy, run } = useAction();
  const [action, setAction] = useState('');
  const { data, loading } = useLoad(() => api.get(`/audit${qs({ action, limit: 300 })}`), [action]);
  const [verify, setVerify] = useState<any>(null);
  return (
    <>
      <PageHead title="Auditoría" subtitle="Registro inalterable de todas las acciones. Cada evento está encadenado con el anterior mediante un hash."
        actions={<button disabled={busy} onClick={async () => setVerify(await run(() => api.get('/audit/verify')))}>Verificar integridad</button>} />
      {verify && <div className={`alert ${verify.ok ? 'ok' : 'error'}`} style={{ marginBottom: 12 }}>{verify.ok ? `Cadena íntegra: ${verify.checked} eventos comprobados.` : `¡Alteración detectada en el evento ${verify.brokenAt}!`}</div>}
      <select value={action} onChange={(e) => setAction(e.target.value)} style={{ maxWidth: 280, marginBottom: 12 }}>
        <option value="">Todas las acciones</option><option value="invoice.">Facturas</option><option value="approval.">Aprobaciones</option><option value="vendor.">Proveedores</option>
        <option value="payment_batch.">Pagos</option><option value="export.">Exportaciones</option><option value="einvoice.">Factura electrónica</option><option value="auth.">Accesos</option><option value="user.">Usuarios</option><option value="company.">Configuración</option>
      </select>
      {loading && !data ? <Loading /> : (
        <div className="table-wrap"><table>
          <thead><tr><th>Fecha</th><th>Quién</th><th>Acción</th><th>Elemento</th><th>Detalle</th></tr></thead>
          <tbody>{(data ?? []).map((a: any) => (
            <tr key={a.id}><td style={{ whiteSpace: 'nowrap' }}>{dateTime(a.created_at)}</td><td>{a.actor_name ?? ({ system: 'Conforme', approval_link: 'Enlace de aprobación', api_key: 'API' } as any)[a.actor_type] ?? a.actor_type}</td>
              <td className="mono">{a.action}</td><td>{a.entity === 'invoice' && a.entity_id ? <Link to={`/facturas/${a.entity_id}`}>factura</Link> : a.entity}</td>
              <td className="hint" style={{ maxWidth: 420, wordBreak: 'break-word' }}>{a.after ? JSON.stringify(a.after).slice(0, 220) : ''}</td></tr>
          ))}</tbody>
        </table></div>
      )}
    </>
  );
}
